import * as React from 'react';
import { FC, useMemo } from 'react';

import { isUndefined } from 'lodash-es';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { ResponsiveCarousel } from 'components/responsive-carousel';
import { useLoyaltyFeaturedRewardsQuery } from 'generated/sanity-graphql';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';

import LoyaltyTierCard from './loyalty-tier-card';
import { Container, HeadingText, StyledLoadingAnimation } from './loyalty-tiers-widget.styled';
import {
  ILoyaltyTiersWidgetProps,
  ISortFunction,
  NullableRewardTier,
  RewardTier,
  Sort,
} from './types';

/**
 *
 * LoyaltyTiersWidget references featured rewards in Sanity along with their assigned categories
 * Loyalty tiers are optionally sortable by point value (controlled via sanity property)
 *
 */
const LoyaltyTiersWidget: FC<ILoyaltyTiersWidgetProps> = ({ title, sortByPointValue }) => {
  const navigate = useNavigate();
  const { data, loading: featuredRewardsLoading } = useLoyaltyFeaturedRewardsQuery();
  const [featuredRewards] = data?.allRewardsSections || [];
  const { engineRewardsMap, loading: rewardsListLoading } = useLoyaltyRewardsList();
  const isLoading = featuredRewardsLoading || rewardsListLoading;
  const { formatMessage } = useIntl();

  const rewardTiersData = useMemo(() => {
    if (isLoading) {
      return [];
    }
    return featuredRewards?.rewardsList?.reduce((acc: RewardTier[], featuredReward) => {
      const { reward, category } = featuredReward || {};
      const engineReward = engineRewardsMap[reward?.loyaltyEngineId || ''];
      const { image, label: categoryLabel } = category || {};
      const { locked } = engineReward || {};

      const rewardTier = {
        image: image?.locale,
        label: categoryLabel?.locale,
        id: reward?._id,
        inactive: !!locked,
      };

      const allDataAvailable = (
        nullableRewardTier: NullableRewardTier
      ): nullableRewardTier is RewardTier => {
        return !Object.values(nullableRewardTier).some(isUndefined);
      };

      if (allDataAvailable(rewardTier)) {
        acc.push(rewardTier);
      }
      return acc;
    }, []);
  }, [engineRewardsMap, featuredRewards, isLoading]);

  if (isLoading) {
    return <StyledLoadingAnimation fillColor={Styles.color.white} />;
  }

  if (!rewardTiersData?.length) {
    return null;
  }

  const sortRewardTiers = () => {
    const fetchPointValue = (id: string) => engineRewardsMap?.[id]?.pointCost || 0;

    const sortFunction: ISortFunction | undefined = {
      [Sort.Asc]: (a: RewardTier, b: RewardTier) => {
        return fetchPointValue(a.id) - fetchPointValue(b.id);
      },
      [Sort.Desc]: (a: RewardTier, b: RewardTier) => {
        return fetchPointValue(b.id) - fetchPointValue(a.id);
      },
    }[sortByPointValue];

    if (sortFunction instanceof Function) {
      return rewardTiersData.sort(sortFunction);
    }

    return rewardTiersData;
  };

  const handleTierClick = ({ label }: RewardTier) => {
    navigate(`${formatMessage({ id: 'routes.loyaltyHome' })}/list?category=${label}`);
  };

  const rewardTiers = sortByPointValue ? sortRewardTiers() : rewardTiersData;

  return (
    <Container data-testid="loyalty-tiers-widget">
      <HeadingText>{title?.locale}</HeadingText>
      <ResponsiveCarousel>
        {rewardTiers.map((tier, idx) => (
          <LoyaltyTierCard key={tier.id} tier={tier} idx={idx} handleTierClick={handleTierClick} />
        ))}
      </ResponsiveCarousel>
    </Container>
  );
};

export default LoyaltyTiersWidget;
